import './meat-agent-reply'
import './meat-mic-waveform'
import { micIcon } from './meat-agent-voice'
import type { AgentWidget, ChatMessage } from '../agent'
import type { AgentReply } from './meat-agent-reply'
import type { AgentVoice } from './meat-agent-voice'
import type { MicWaveform } from './meat-mic-waveform'

const template = document.createElement('template')
template.innerHTML = `
  <style>
    :host {
      display: flex;
      flex-direction: column;
      min-width: 0;
      min-height: 0;
      border-radius: var(--radius-md);
      background: var(--color-surface);
      color: var(--color-text);
      overflow: hidden;
    }
    header {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      padding: var(--space-2-5) var(--space-3);
      background: var(--color-surface-header);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-bold);
    }
    header::before {
      content: '';
      width: var(--space-2);
      height: var(--space-2);
      border-radius: 50%;
      background: var(--color-accent);
    }
    :host([working]) header::before {
      animation: pulse 1.5s ease-in-out infinite;
    }
    @keyframes pulse {
      50% { opacity: 0.4; }
    }
    meat-agent-voice {
      --voice-radius: 50%;
      width: 1.75rem;
      height: 1.75rem;
      /* It's taller than the name, but shouldn't make the header any taller. */
      margin: calc(-1 * var(--space-1)) 0 calc(-1 * var(--space-1)) auto;
    }
    main {
      flex: 1;
      padding: var(--space-4);
      overflow-y: auto;
      overflow-wrap: anywhere;
      line-height: var(--line-height);
    }
    /* Until the agent's prompted, what it does sits in the middle. */
    .empty {
      display: grid;
      place-items: center;
      box-sizing: border-box;
      height: 100%;
      margin: 0;
      color: var(--color-text-subtle);
      font-style: italic;
      text-align: center;
    }
    .empty[hidden] {
      display: none;
    }
    .working {
      display: flex;
      gap: var(--space-1);
      padding: var(--space-1) 0;
    }
    meat-agent-reply + .working {
      margin-top: var(--space-2);
    }
    .working[hidden] {
      display: none;
    }
    .working span {
      width: var(--space-1-5);
      height: var(--space-1-5);
      border-radius: 50%;
      background: var(--color-text-muted);
      animation: bounce 1.2s ease-in-out infinite;
    }
    .working span:nth-child(2) { animation-delay: 0.15s; }
    .working span:nth-child(3) { animation-delay: 0.3s; }
    @keyframes bounce {
      0%, 60%, 100% { opacity: 0.3; transform: none; }
      30% { opacity: 1; transform: translateY(-0.25rem); }
    }
    /* While we're talking to the agent, the mic takes the place of what it said. */
    .listening {
      --mic-size: 5rem;
      display: none;
      flex: 1;
      grid-template: "listening" minmax(0, 1fr) / minmax(0, 1fr);
      place-items: center;
      min-height: 0;
    }
    :host([listening]) main {
      display: none;
    }
    :host([listening]) .listening {
      display: grid;
    }
    .listening > * {
      grid-area: listening;
    }
    /* The loudest bars are as tall as the mic. */
    meat-mic-waveform {
      width: 100%;
      height: var(--mic-size);
    }
    .listening svg {
      position: relative;
      width: var(--mic-size);
      height: var(--mic-size);
      fill: var(--color-accent-strong);
    }
    .status {
      margin: 0;
      padding: var(--space-2) var(--space-3);
      background: var(--color-surface-footer);
      color: var(--color-text-muted);
      font-size: var(--font-size-sm);
    }
    .status:empty {
      display: none;
    }
  </style>
  <header>
    <span class="name"></span>
    <meat-agent-voice accent hidden></meat-agent-voice>
  </header>
  <main>
    <p class="empty">Joined the meeting</p>
    <div class="working" role="status" aria-label="Working" hidden><span></span><span></span><span></span></div>
  </main>
  <div class="listening">
    <meat-mic-waveform></meat-mic-waveform>
    ${micIcon}
  </div>
  <p class="status" role="status"></p>
`

/**
 * The default widget for an agent in the meeting: just the latest thing it
 * said, replaced as soon as it starts saying something new. Its `name`
 * attribute is the agent's display name, and its `description`, if it has
 * one, shows in the middle until the agent's first prompted. While a prompt is waiting for its
 * reply the host has the `working` attribute. The `voice` button in the
 * corner prompts the agent by voice. While it's listening, the host has the
 * `listening` attribute and shows a big mic over what our `mic` hears in
 * place of the reply. How the voice prompt is going shows along the bottom.
 * The same goes for anyone else in the room prompting the agent by voice,
 * with their audio in place of our mic and their name along the bottom.
 */
export class AgentCard extends HTMLElement implements AgentWidget {
  static observedAttributes = ['name', 'description']

  #root: ShadowRoot
  #name: HTMLElement
  #body: HTMLElement
  #empty: HTMLElement
  #working: HTMLElement
  #status: HTMLElement
  #waveform: MicWaveform
  readonly voice: AgentVoice
  mic?: MediaStream
  // The reply on show, and the stream it's coming in on until it's complete.
  #reply?: AgentReply
  #stream?: string
  // Whether the reply on show is still streaming in.
  #streaming = false
  // Streams replaced by something newer before they finished.
  #replaced = new Set<string>()
  // Prompts the agent hasn't answered yet, oldest first.
  #waiting: string[] = []
  // Others in the room prompting the agent by voice, by peer.
  #remoteVoices = new Map<string, { name: string; stream?: MediaStream }>()

  constructor() {
    super()
    this.#root = this.attachShadow({ mode: 'open' })
    this.#root.append(template.content.cloneNode(true))
    this.#name = this.#root.querySelector('.name')!
    this.#body = this.#root.querySelector('main')!
    this.#empty = this.#root.querySelector('.empty')!
    this.#working = this.#root.querySelector('.working')!
    this.#status = this.#root.querySelector('.status')!
    this.#waveform = this.#root.querySelector('meat-mic-waveform')!
    this.voice = this.#root.querySelector('meat-agent-voice')!
    this.voice.addEventListener('voice-change', () => this.#updateVoice())
  }

  attributeChangedCallback() {
    this.#name.textContent = this.getAttribute('name') ?? 'Agent'
    this.#empty.textContent = this.getAttribute('description') || 'Joined the meeting'
  }

  addPrompt(msg: ChatMessage) {
    this.#empty.hidden = true
    this.#waiting.push(msg.id)
    this.#updateWorking()
  }

  addReplyChunk(id: string, text: string) {
    if (id !== this.#stream) {
      this.#show('', id)
      this.#streaming = true
      this.#updateWorking()
    }
    const reply = this.#reply!
    this.#follow(() => reply.addText(text))
  }

  addReply(msg: ChatMessage) {
    if (msg.stream && msg.stream === this.#stream) {
      const reply = this.#reply!
      this.#follow(() => reply.setText(msg.text))
      this.#stream = undefined
      this.#streaming = false
    } else if (msg.stream && this.#replaced.delete(msg.stream)) {
      // A streamed reply that's since been replaced is already out of date.
    } else {
      // Including a streamed reply we never saw the start of, as when
      // catching up on the conversation after joining.
      this.#show(msg.text)
      this.#streaming = false
    }

    // The agent answers prompts in order, so any before this one are done too.
    const answered = this.#waiting.indexOf(msg.inReplyTo ?? '')
    if (answered >= 0) this.#waiting.splice(0, answered + 1)
    this.#updateWorking()
  }

  // Thoughts and tool calls aren't shown, but mean the agent has stopped
  // writing its reply for now.
  addThinking() {
    this.#endReply()
  }

  addToolCall() {
    this.#endReply()
  }

  addToolResult() {}

  setVoicePrompt(peer: string, name: string, stream: MediaStream | undefined, active: boolean) {
    if (active) this.#remoteVoices.set(peer, { name, stream })
    else this.#remoteVoices.delete(peer)
    this.#updateVoice()
  }

  // Shows the big mic while anyone's prompting the agent by voice, over our
  // own mic if it's us and the first other speaker's audio if not.
  #updateVoice() {
    const remote = [...this.#remoteVoices.values()]
    const names = remote.map(v => v.name)
    const others = names.length === 0 ? ''
      : names.length === 1 ? `${names[0]} is voice prompting…`
      : `${names.slice(0, -1).join(', ')} and ${names.at(-1)} are voice prompting…`
    this.#status.textContent = this.voice.hint || others || this.voice.message

    const listening = this.voice.listening || remote.length > 0
    this.toggleAttribute('listening', listening)
    const stream = this.voice.listening ? this.mic : remote[0]?.stream
    // Setting the stream starts the waveform afresh.
    if (this.#waveform.stream !== stream) this.#waveform.stream = stream
  }

  // Replaces the reply on show with a new one.
  #show(text: string, stream?: string) {
    const reply = document.createElement('meat-agent-reply')
    reply.setText(text)
    this.#empty.hidden = true
    if (this.#stream) this.#replaced.add(this.#stream)
    if (this.#reply) this.#reply.replaceWith(reply)
    else this.#body.insertBefore(reply, this.#working)
    this.#reply = reply
    this.#stream = stream
    this.#body.scrollTop = 0
  }

  #endReply() {
    if (!this.#streaming) return
    this.#streaming = false
    this.#updateWorking()
  }

  // The dots show while prompts wait, unless a reply is visibly streaming in.
  #updateWorking() {
    const working = this.#waiting.length > 0
    this.#working.hidden = !working || this.#streaming
    this.toggleAttribute('working', working)
  }

  // Makes a change to the reply, following it as it grows unless the user has scrolled back.
  #follow(change: () => void) {
    const body = this.#body
    const atBottom = body.scrollHeight - body.scrollTop - body.clientHeight < 8
    change()
    if (atBottom) body.scrollTop = body.scrollHeight
  }
}

customElements.define('meat-agent-card', AgentCard)

declare global {
  interface HTMLElementTagNameMap {
    'meat-agent-card': AgentCard
  }
}
